import type { ReactNode } from "react";
import { Plus, Save, Trash2, Download, LogIn } from "lucide-react";
import { Button } from "@maeiz/ui";
import { DocPage, Section, Demo, SpecTable, DoDont } from "../../components/DocChrome";

export function ButtonsPage() {
    return (
        <DocPage
            title="Buttons"
            description="Modern-minimal Workspace actions. No hover scale. Optional dynamic icon prefix via icon / iconPosition."
        >
            <Section title="1. Anatomy">
                <p>
                    Container · optional start/end icon · label · optional loading spinner. Focus
                    ring is separate from fill.
                </p>
            </Section>
            <Section title="2. Purpose">
                <p>
                    Trigger discrete actions. One primary button per view region. Gold is
                    brand-critical only — never delete.
                </p>
            </Section>
            <Section title="3. Variants">
                <Demo title="How it looks">
                    <div className="flex flex-wrap gap-3">
                        <Button variant="primary" icon={Plus}>
                            Create
                        </Button>
                        <Button variant="secondary" icon={Download}>
                            Export
                        </Button>
                        <Button variant="gold" icon={Save}>
                            Save premium
                        </Button>
                        <Button variant="outline" icon={LogIn}>
                            Sign in
                        </Button>
                        <Button variant="outline-gold">Outline gold</Button>
                        <Button variant="ghost">Ghost</Button>
                        <Button variant="danger" icon={Trash2}>
                            Delete
                        </Button>
                        <Button variant="tertiary">Warning</Button>
                    </div>
                </Demo>
            </Section>
            <Section title="4. Sizes">
                <Demo>
                    <div className="flex flex-wrap items-center gap-3">
                        <Button size="sm" icon={Plus}>
                            Small
                        </Button>
                        <Button size="md" icon={Plus}>
                            Medium
                        </Button>
                        <Button size="lg" icon={Plus}>
                            Large
                        </Button>
                    </div>
                </Demo>
            </Section>
            <Section title="5. States">
                <Demo title="8-state matrix (forced classes where needed)">
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Row label="default">
                            <Button icon={Save}>Save</Button>
                        </Row>
                        <Row label="hover">
                            <Button icon={Save} className="!bg-primary-dark">
                                Save
                            </Button>
                        </Row>
                        <Row label="focus-visible">
                            <Button icon={Save} className="ring-2 ring-primary ring-offset-2">
                                Save
                            </Button>
                        </Row>
                        <Row label="active">
                            <Button icon={Save} className="!bg-primary-dark">
                                Save
                            </Button>
                        </Row>
                        <Row label="disabled">
                            <Button icon={Save} disabled>
                                Save
                            </Button>
                        </Row>
                        <Row label="loading">
                            <Button icon={Save} isLoading>
                                Save
                            </Button>
                        </Row>
                        <Row label="danger">
                            <Button variant="danger" icon={Trash2}>
                                Delete
                            </Button>
                        </Row>
                        <Row label="icon end">
                            <Button icon={Download} iconPosition="end">
                                Download
                            </Button>
                        </Row>
                    </div>
                </Demo>
            </Section>
            <Section title="6. Behavior">
                <ul className="list-disc space-y-1 ps-5">
                    <li>
                        <strong>Motion:</strong> none — no scale, no lift, no Framer on buttons.
                    </li>
                    <li>
                        <strong>icon:</strong> optional <code>LucideIcon</code> or ReactNode;
                        default position <code>start</code>.
                    </li>
                    <li>
                        <strong>Loading:</strong> disables control; spinner replaces prefix icon.
                    </li>
                </ul>
            </Section>
            <Section title="7. Responsive">
                <p>
                    On xs, prefer fullWidth for solitary primary CTAs in forms. Icon + label stays
                    single-line (no wrap).
                </p>
            </Section>
            <Section title="8. Accessibility">
                <SpecTable
                    rows={[
                        ["Element", "native button"],
                        ["Focus", "focus-visible ring, instant"],
                        ["Loading", "disabled + data-state=loading"],
                        ["Icons", "aria-hidden decorative"],
                        ["Icon-only", "require aria-label on the button"],
                    ]}
                />
            </Section>
            <Section title="9. Usage">
                <Demo title="Developer">
                    <pre className="overflow-x-auto rounded-mz-md bg-neutral-ink p-4 text-xs text-gold-light">{`import { Button } from "@maeiz/ui";
import { Plus } from "lucide-react";

<Button variant="primary" icon={Plus}>Create customer</Button>
<Button icon={Download} iconPosition="end">Export</Button>`}</pre>
                </Demo>
            </Section>
            <Section title="10. Do / Don't">
                <DoDont
                    doItems={[
                        "Pass icon when the action benefits from recognition",
                        "Use danger for irreversible deletes",
                        "Keep one primary per region",
                    ]}
                    dontItems={[
                        "Animate scale/lift on hover",
                        "Use gold for destructive actions",
                        "Require icons on every button (optional API)",
                    ]}
                />
            </Section>
            <Section title="11. Composition">
                <p>Compose with DropdownMenuTrigger asChild, Modal footers, PageHeader actions.</p>
            </Section>
            <Section title="12. Related">
                <p>
                    IconButton pattern → Button + aria-label; DropdownMenu; Toast for silent vs
                    explicit success.
                </p>
            </Section>
        </DocPage>
    );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-mz-lg border border-neutral-border px-3 py-2 dark:border-white/10">
            <span className="font-mono text-xs text-neutral-secondary">{label}</span>
            {children}
        </div>
    );
}
